import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material';
import { XIcon, ClockIcon, GlobeIcon, LockIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { getAgentToolApi } from '../../../../utils/apis';
import { UltravoxToolData } from '../../../../schemas/apis/interface';

interface ToolDetailsProps {
  open: boolean;
  onClose: () => void;
  toolId: string | null;
}

const ToolDetails: React.FC<ToolDetailsProps> = ({ open, onClose, toolId }) => {
  const {
    data: toolData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['tool-detail', toolId],
    queryFn: () => getAgentToolApi(toolId!),
    enabled: !!toolId && open,
    refetchOnWindowFocus: false,
  });

  const tool: UltravoxToolData | undefined = toolData?.data?.data;

  const renderParameterTable = (parameters: any[], emptyMessage: string) => {
    if (!parameters || parameters.length === 0) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Required</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.map((param: any, index: number) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'DM Sans' }}>
                    {param.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f5f5f5',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      display: 'inline-block',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    {param.type || param.schema?.type || 'unknown'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {param.schema?.description ||
                      param.description ||
                      'No description'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: param.required ? '#d32f2f' : '#666',
                      backgroundColor: param.required ? '#ffebee' : '#f5f5f5',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      display: 'inline-block',
                      border: param.required
                        ? '1px solid #ffcdd2'
                        : '1px solid #e0e0e0',
                    }}
                  >
                    {param.required ? 'Yes' : 'No'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderHeaderParameterTable = (
    parameters: any[],
    emptyMessage: string
  ) => {
    if (!parameters || parameters.length === 0) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Value</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parameters.map((param: any, index: number) => (
              <TableRow key={index}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'DM Sans' }}>
                    {param.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: '#f5f5f5',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      display: 'inline-block',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    {param.value || 'No value'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: '#1976d2',
                      backgroundColor: '#e3f2fd',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      display: 'inline-block',
                      border: '1px solid #90caf9',
                    }}
                  >
                    Header
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Helper function to separate header and non-header parameters
  const separateParametersByLocation = (parameters: any[]) => {
    if (!parameters) return { headerParams: [], otherParams: [] };

    const headerParams = parameters.filter(
      param => param.location === 'PARAMETER_LOCATION_HEADER'
    );
    const otherParams = parameters.filter(
      param => param.location !== 'PARAMETER_LOCATION_HEADER'
    );

    return { headerParams, otherParams };
  };

  const renderLoadingSkeleton = () => (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
      <Skeleton variant="text" width="40%" height={24} sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" height={100} sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" height={200} />
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '637px',
            p: 0,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
          },
        },
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#FFFFFF',
          px: 2,
          pt: 2,
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <XIcon size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              Tool Details
            </Typography>
          </Box>
        </Box>
        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 1,
            mx: -2,
          }}
        />
      </Box>

      {/* Content Section */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2 }}>
        {isLoading ? (
          renderLoadingSkeleton()
        ) : error ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '200px',
              gap: 2,
            }}
          >
            <Typography color="error">Failed to load tool details</Typography>
            <Typography variant="body2" color="text.secondary">
              {error instanceof Error ? error.message : 'Unknown error'}
            </Typography>
          </Box>
        ) : tool ? (
          <Box sx={{ pt: 2 }}>
            {/* Tool Info Section */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: 'custom.textBlue' }}
                  >
                    {tool.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {tool.definition.description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {tool.ownership === 'private' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: '1px solid #ce93d8',
                        fontSize: '0.875rem',
                      }}
                    >
                      <LockIcon size={14} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Private
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        px: 1.5,
                        py: 0.5,

                        fontSize: '0.875rem',
                      }}
                    >
                      <GlobeIcon size={14} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Public
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* HTTP Configuration */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                >
                  HTTP Configuration
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Method
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'DM Sans', fontWeight: 500 }}
                    >
                      {tool.definition.http?.httpMethod}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Timeout
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <ClockIcon size={14} />
                      <Typography variant="body2">
                        {tool.definition.timeout}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">
                      Base URL
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'DM Sans',
                        wordBreak: 'break-all',
                        backgroundColor: '#f5f5f5',
                        p: 1,
                        borderRadius: 1,
                        mt: 0.5,
                      }}
                    >
                      {tool.definition.http?.baseUrlPattern}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Configuration */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                >
                  Configuration
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Precomputable
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: tool.definition.precomputable
                          ? '#2e7d32'
                          : '#666',
                        backgroundColor: tool.definition.precomputable
                          ? '#e8f5e8'
                          : '#f5f5f5',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        display: 'inline-block',
                        border: tool.definition.precomputable
                          ? '1px solid #a5d6a7'
                          : '1px solid #e0e0e0',
                        mt: 0.5,
                      }}
                    >
                      {tool.definition.precomputable ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Model Tool Name
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'DM Sans', fontWeight: 500 }}
                    >
                      {tool.definition.modelToolName}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Default Reaction
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: '#1976d2',
                        backgroundColor: '#e3f2fd',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        display: 'inline-block',
                        border: '1px solid #90caf9',
                        mt: 0.5,
                      }}
                    >
                      {tool.definition.defaultReaction ===
                      'AGENT_REACTION_SPEAKS'
                        ? 'Speaks'
                        : tool.definition.defaultReaction ===
                            'AGENT_REACTION_LISTENS'
                          ? 'Listens'
                          : tool.definition.defaultReaction ===
                              'AGENT_REACTION_SPEAKS_ONCE'
                            ? 'Speaks Once'
                            : 'Default'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Static Response */}
              {tool.definition.staticResponse?.responseText && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                  >
                    Static Response
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      p: 2,
                      borderRadius: 1,
                      fontFamily: 'DM Sans',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {tool.definition.staticResponse.responseText}
                  </Typography>
                </Paper>
              )}

              {/* Parameters Sections */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                >
                  Dynamic Parameters
                </Typography>
                {renderParameterTable(
                  tool.definition.dynamicParameters,
                  'No dynamic parameters defined'
                )}
              </Box>

              {/* Header Parameters Section */}
              {(() => {
                const { headerParams } = separateParametersByLocation(
                  tool.definition.staticParameters
                );

                if (headerParams.length > 0) {
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          color: 'custom.textBlue',
                        }}
                      >
                        Header
                      </Typography>
                      {renderHeaderParameterTable(
                        headerParams,
                        'No header parameters defined'
                      )}
                    </Box>
                  );
                }
                return null;
              })()}

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                >
                  Static Parameters
                </Typography>
                {(() => {
                  const { otherParams } = separateParametersByLocation(
                    tool.definition.staticParameters
                  );
                  return renderParameterTable(
                    otherParams,
                    'No static parameters defined'
                  );
                })()}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                >
                  Automatic Parameters
                </Typography>
                {renderParameterTable(
                  tool.definition.automaticParameters,
                  'No automatic parameters defined'
                )}
              </Box>

              {/* Requirements */}
              {tool.definition.requirements &&
                Object.keys(tool.definition.requirements).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 2, color: 'custom.textBlue' }}
                    >
                      Requirements
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <pre
                        style={{
                          margin: 0,
                          fontSize: '0.875rem',
                          fontFamily: 'DM Sans',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {JSON.stringify(tool.definition.requirements, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                )}
            </Box>
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
};

export default ToolDetails;
